// src/features/dashboard/mocks/teamTable.mock.ts

import type {
  Employee,
  LegendItem,
  HoursDataItem,
  DayItem,
} from '../types/teamTable.types';

import type {
  DashboardGridResponse,
} from '../services/TableTeamApi';

const COLORS = [
  'bg-[#3B82F6]',
  'bg-[#10B981]',
  'bg-[#8B5CF6]',
  'bg-[#F59E0B]',
  'bg-[#EC4899]',
  'bg-[#06B6D4]',
];

const minutesToHHMM = (
  minutes: number,
): string => {

  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;

  return `${hrs
    .toString()
    .padStart(2, '0')}:${mins
    .toString()
    .padStart(2, '0')}`;
};

const getInitials = (
  name: string,
): string =>
  name
    .split(' ')
    .map((x) => x[0])
    .join('');

const getDayLabel = (
  date: string,
): string =>
  new Date(date)
    .toLocaleDateString(
      'en-US',
      {
        weekday: 'short',
      },
    )
    .toUpperCase();

type TeamTableMappedData = {
  EMPLOYEES: Employee[];
  DAYS: DayItem[];
  TEAM_AVERAGES: string[];
};

/**
 * =========================================
 * MAP API RESPONSE
 * =========================================
 */
export const mapTeamTableData = (
  data: DashboardGridResponse,
): TeamTableMappedData => {

  const employeeMap =
  new Map<string, Employee>();

  data.employees.forEach(
    (
      item,
      index,
    ) => {

      if (
        !employeeMap.has(item.employeeCode)
      ) {

   employeeMap.set(
  item.employeeCode,
  {
    employeeCode: item.employeeCode,

    loggedDate: item.loggedDate,

    initials: getInitials(item.employeeName),

    color: COLORS[index % COLORS.length],

    name: item.employeeName,

    geo: item.geo, // ✅ ADD THIS

    role: `${item.designation} · ${item.geo}`,

    total: minutesToHHMM(item.totalMinutes),

    avg: `${minutesToHHMM(item.avgMinutesPerDay)}/day`,

    hoursData: [],
  },
);
      }

      const employee =
       employeeMap.get(
    item.employeeCode,
);

      if (employee) {

        const hourItem: HoursDataItem = {
          displayValue:
            item.displayValue,

          loggedDate:
            item.loggedDate.split(
              'T',
            )[0],
        };

        employee.hoursData.push(
          hourItem,
        );
      }
    },
  );

  return {

    EMPLOYEES:
      Array.from(
        employeeMap.values(),
      ),

    DAYS:
      data.teamAverages.map(
        (item) => ({
          day:
            new Date(
              item.loggedDate,
            )
              .getDate()
              .toString(),

          label:
            getDayLabel(
              item.loggedDate,
            ),
        }),
      ),

    TEAM_AVERAGES:
      data.teamAverages.map(
        (item) =>
          item.teamAverageDisplay,
      ),
  };
};

/**
 * =========================================
 * MOCK DATA
 * =========================================
 */
export const teamTableMockData: DashboardGridResponse =
{
  employees: [
    {
      employeeCode: "SG23232",
      employeeName: 'John Smith',
      designation: 'Team Lead',
      geo: 'India',
      loggedDate: '2026-06-01',
      displayValue: '08:30',
      totalMinutes: 510,
      avgMinutesPerDay: 510,
    },
    {
      employeeCode: "69483",
      employeeName: 'John Smith',
      designation: 'Team Lead',
      geo: 'India',
      loggedDate: '2026-06-02',
      displayValue: '09:10',
      totalMinutes: 550,
      avgMinutesPerDay: 530,
    },
    {
      employeeCode: "83434",
      employeeName: 'David Wilson',
      designation: 'Associate',
      geo: 'India',
      loggedDate: '2026-06-01',
      displayValue: '07:45',
      totalMinutes: 465,
      avgMinutesPerDay: 465,
    },
  ],

  teamAverages: [
    {
      loggedDate: '2026-06-01',
      teamAverageDisplay: '08:05',
    },
    {
      loggedDate: '2026-06-02',
      teamAverageDisplay: '08:20',
    },
  ],
};

/**
 * =========================================
 * LEGENDS
 * =========================================
 */
export const LEGENDS: LegendItem[] = [
  ['#FDE68A', '<5h'],
  ['#FCD34D', '5-7h'],
  ['#A7F3D0', '7-8h'],
  ['#6EE7B7', '8-9h'],
  ['#10B981', '>9h'],
];